/* eslint-disable no-nested-ternary */

import React, { useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Input from 'src/components/ui/Input/Input';
import ModalListItem from 'src/components/layouts/ModalListItem/ModalListItem';
import Modal from '../../layouts/Modal/Modal';
import './SelectVariableModal.scss';

export type ListItem = {
  variableName: string;
  variableLabel: string;
};

export type SelectVariableModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onItemSelect?: (selectedItem: string) => void;
  listItems: ListItem[];
  isLoading?: boolean;
  /** Modal heading; defaults to the classic variable-picker title. */
  title?: string;
};

const SelectVariableModal: React.FC<SelectVariableModalProps> = ({
  isOpen,
  onClose,
  listItems,
  onItemSelect,
  isLoading = false,
  title,
}) => {
  const { t } = useTranslation();
  const heading = title ?? t('general.insert_variable');
  const sortItemsByVariableName = (items: ListItem[]) => {
    return items.sort((a, b) => a.variableName.localeCompare(b.variableName));
  };

  const [selectedVariable, setSelectedVariable] = useState('');
  const [filteredItems, setFilteredItems] = useState<ListItem[]>(sortItemsByVariableName(listItems));

  const handleVariableChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSelectedVariable(value);
    const filtered = listItems.filter(item => item.variableName.toLowerCase().includes(value.toLowerCase()));
    setFilteredItems(filtered);
  };

  const handleItemClick = (variableName: string) => {
    if (onItemSelect) {
      onItemSelect(variableName);
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} hasBackdrop={false} hasCloseButton smallSize>
      <div className="selectVariableModal h-[320px] mx-auto surface-elevated rounded-lg shadow-lg">
        <div className="flex flex-col md:flex-row justify-between h-[310px]">
          <div className="mt-5">
            <div className="flex flex-col gap-8 items-start p-14">
              <h2 className="typo-text-12--bold">{heading}</h2>
              <Input
                value={selectedVariable}
                icon="search"
                onChange={handleVariableChange}
                placeholder={t('general.search_placeholder')}
                clearable={!!selectedVariable}
              />
            </div>
            <ul className="list-none px-14 variablesSection h-[220px] overflow-x-hidden">
              {isLoading ? (
                <p className="typo-text-14 text-faded">{t('general.loading_variables')}</p>
              ) : filteredItems.length ? (
                filteredItems.map(item => (
                  <ModalListItem
                    key={item.variableName}
                    item={item}
                    onClick={() => handleItemClick(item.variableName)}
                    onKeyDown={() => handleItemClick(item.variableName)}
                    type="variableName"
                  />
                ))
              ) : (
                <p className="typo-text-14 text-faded">{t('general.no_data')}</p>
              )}
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SelectVariableModal;
